<script lang="ts">
    import {
        mdiArrowDecisionAuto,
        mdiClipboardText,
        mdiPageNext,
        mdiTag,
        mdiTrashCanOutline,
        mdiRestore,
        mdiFormatListGroupPlus,
        mdiPencilOutline,
    } from "@mdi/js"

    import { debounce, _ as _l } from "lodash"
    import Modal from "../../widgets/Modal.svelte"
    import { getOperation, updateCart } from "../../../api"
    import { icons, modelGroupCodesMap } from "../../../config"
    import { tooltip } from "../../../helper"
    import { openOverlay, showPrices, newNotification, carts, login, filesStore } from "../../../store"
    import NumberField from "../../widgets/fields/NumberField.svelte"
    import PositionImageField from "../../widgets/fields/PositionImageField.svelte"
    import PositionStatusField from "../../widgets/fields/PositionStatusField.svelte"
    import TrackingField from "../../widgets/fields/TrackingField.svelte"
    import WeekField from "../../widgets/fields/WeekField.svelte"
    import Icon from "../../widgets/Icon.svelte"
    import MiniLoader from "../../widgets/MiniLoader.svelte"
    import PositionDetails from "./PositionDetails.svelte"
    import OperationPositionPriceSummary from "./OperationPositionPriceSummary.svelte"
    import ChargeableField from "../../widgets/fields/ChargeableField.svelte"
    import { createEventDispatcher } from "svelte"
    import Checkbox from "../../widgets/Checkbox.svelte"
    import { onMount } from "svelte"
    import TextField from "./edit/fields/TextField.svelte"
    import AdditionalServices from "./positions/AdditionalServices.svelte"
    import AdditionalPositions from "./positions/AdditionalPositions.svelte"
    import Pricewizard from "./edit/pricewizard/Pricewizard.svelte"
    import { updatePriceWizard } from "../../../functions/operations/pricegroup"
    import { isOperationOut, asOperationOut, asOperation } from "../../../functions/operations/typeGuards"
    import { _ } from "svelte-i18n"

    export let rowId: any = ""
    export let operation: OperationOut | Operation
    export let activePosition: number = -1
    export let loadAttachments: (op: Operation | OperationOut) => Promise<Attachement[]> | undefined
    export let sharedView: boolean = false
    // undefined = Ansicht nach Fensterbreite (siehe class:gridview/tableview)
    export let grid: boolean = undefined
    export let cart = false
    // nur im Warenkorb gesetzt; außerhalb von cart-Aufrufern nicht vorhanden
    export let cartObj: Cart = undefined
    export let inCart = false
    export let cartId = ""
    export let overlay = false

    let showMarginWarning = false

    const dispatch = createEventDispatcher()
    let deletePositionOperation: OperationPositionOut
    let restorePositionOperation: OperationPositionOut
    // Warenkorb-Vorgänge sind immer `operation` (operation-outs werden dort
    // nie eingehängt), daher ist hier der engere Typ korrekt.
    let deleteOperation: Operation

    let innerWidth = window.innerWidth
    const gridBreakPoint = 1350

    let positionHeights: {
        [key: number]: number
    } = {}
    let positionTab: {
        [key: number]: "text" | "connected"
    } = {}

    const runningProms: {
        [n: number]: Promise<{ count?: number; data: Operation[] }>
    } = {}

    async function getOpByNumber(n: number) {
        if (!runningProms[n]) {
            runningProms[n] = getOperation(operation.collectionName || "operation", {
                number: n,
            })
        }
        const r = await runningProms[n]
        return r.data?.length ? r.data[0] : null
    }
    const debounceFn = async (cartObj: Cart) => {
        await updateCart(cartObj)
            .then(() => {
                let i = $carts.indexOf(cartObj)
                if (i != -1) {
                    $carts[i] = cartObj
                }
                newNotification({
                    class: "success",
                    html: $_("cart.updateSuccessNotification"),
                })
            })
            .catch((e) => {
                newNotification({
                    class: "error",
                    html: $_("cart.updateErrorNotification"),
                })
            })
    }
    const updateCartDebounce = debounce((cartObj: Cart) => {
        debounceFn(cartObj)
    }, 500)

    async function deleteOperationFromCart(operation: Operation) {
        cartObj.operations = cartObj.operations.filter((o) => o.id != operation.id)
        await debounceFn(cartObj)
        deleteOperation = null
        dispatch("operationDeletion")
    }

    async function deselectPositionFromOperation(position: OperationPosition | OperationPositionOut) {
        operation.positions.forEach((pos) => {
            if (_l.isEqual(pos, position)) {
                if (pos.orderQuantity > 0) pos.quantity = pos.orderQuantity
                pos.orderQuantity = 0
            }
        })
        deletePositionOperation = null
        if (cart && location.pathname.includes("cart")) updateCartDebounce(cartObj)
        dispatch("positionSeletion", { operation })
    }

    async function selectPositionFromOperation(position: OperationPosition | OperationPositionOut) {
        operation.positions.forEach((pos) => {
            if (_l.isEqual(pos, position)) {
                if (pos.orderQuantity == 0) pos.orderQuantity = pos.quantity
            }
        })
        restorePositionOperation = null
        if (cart && location.pathname.includes("cart")) updateCartDebounce(cartObj)
        dispatch("positionSeletion", { operation })
    }

    async function deselectOrSelectPositionFromOperation(position: OperationPosition | OperationPositionOut) {
        if (position.orderQuantity == 0) {
            position.orderQuantity = position.quantity
        } else {
            position.orderQuantity = 0
        }
        if (cart) {
            cartObj.operations.forEach((o, i) => {
                if (o.id == operation.id) {
                    cartObj.operations[i] = asOperation(operation)
                }
            })
            if (!inCart) {
                updateCartDebounce(cartObj)
            }
        }
        dispatch("positionSeletion", { operation })
    }

    let header: HTMLDivElement
    function updateVisibleHeader() {
        if (!cart || grid) return
        const tableRect = header.getBoundingClientRect()
        const sep = document.getElementById(`sep${rowId}${cartId}`)?.getBoundingClientRect()
        if (!sep) return
        if (tableRect.bottom <= sep.top) {
            header.classList.add("stick-pos")
        } else {
            if (header.classList.contains("stick-pos")) {
                header.classList.remove("stick-pos")
            }
        }
    }

    onMount(() => {
        updateVisibleHeader()
    })

    function filterValueUpdates(path: string) {
        let logs = operation.updateLogs
        if (!logs) return
        let valueUpdates = logs.filter((obj: { field: string }) => obj.field == path)
        let finalString = ""
        const getPrevious = (previous: string) => {
            if (previous == "null") return $_("positions.noPreviousEntry")
            if (previous == "true") return $_("general.yes")
            if (previous == "false") return $_("general.no")
            return previous
        }

        valueUpdates.forEach((o) => {
            finalString += `<strong style="">${$_("positions.previousValue")}:</strong> ${getPrevious(
                o.previous
            )} <br/>`
        })

        return finalString
    }

    function hasValueUpdates(path: string) {
        let logs = operation?.updateLogs
        if (!logs) return
        let valueUpdates = logs.filter((obj) => obj.field == path)
        return valueUpdates.length > 0 ? "marked" : ""
    }
    let initValueClerk = ""

    const addNewPositionService = (position: OperationPosition | OperationPositionOut) => {
        if (!position.additionalItems) {
            position.additionalItems = []
        }
        if (position?.additionalItems?.length == 0) {
            position.additionalItems.push({
                amount: 1,
                number: 0,
                service: $_("positions.packagingAndShipping"),
                description: $_("positions.packagingAndShippingDescription"),
                netPrice: 0,
            })
            operation.positions = operation.positions
            return
        }
        // Calculate new number for new service
        let highestServiceNumber: number = 0
        if (position.additionalItems?.length) {
            position.additionalItems.forEach((service) => {
                if (service.number > highestServiceNumber) {
                    highestServiceNumber = service.number
                }
            })
        }

        position.additionalItems.push({
            number: highestServiceNumber + 1,
            service: "",
            description: "",
            netPrice: 0,
            amount: 1,
        })

        // Force template update after change position quantity
        operation.positions = operation.positions
    }
    /**
     * Svelte/TS verengt `operation` nicht über `collectionName`-Vergleiche im
     * Template. Diese Zugriffe kapseln daher die (nur auf operation-out
     * vorhandenen) Mail-Statusfelder hinter einem Guard.
     */
    const operationEmailed = (op: Operation | OperationOut): boolean =>
        isOperationOut(op) && (op.statusSummary?.emailed == 100 || !!op.statusSummary?.orderConfimationEmailed)

    /**
     * Prüft, ob die übergebene Position die einzige noch aktive ist
     * (nicht gelöscht / mit Bestellmenge). Wird beim Löschen aus dem
     * Warenkorb verwendet, um zu entscheiden, ob der ganze Vorgang verschwindet.
     */
    const isOnlyActivePosition = (
        op: Operation | OperationOut,
        position: OperationPosition | OperationPositionOut
    ): boolean => {
        const active = op.positions.filter(
            (pos) => isNaN(pos.orderQuantity) || pos.orderQuantity == null || pos.orderQuantity > 0
        )
        return active.length == 1 && active[0].number == position.number
    }

    // Svelte verengt `operation` nicht über Template-Bedingungen; für die
    // operation-out-Blöcke steht damit eine typ-sichere Sicht bereit.
    $: operationOut = asOperationOut(operation)
    // Position des aktuell iterierten Vorgangs als operation-out-Sicht
    // (AdditionalServices wird nur im operation-out-Kontext gerendert).
    const positionOutOf = (pos: OperationPosition | OperationPositionOut): OperationPositionOut =>
        pos as OperationPositionOut

    // Index der Position, für die der Preiswizard geöffnet ist; null = geschlossen.
    let showPricewizard: number | null = null
    const openPricewizard = (idx: number) => {
        showPricewizard = idx
    }
    const closePricewizard = () => {
        showPricewizard = null
    }
</script>

<svelte:window bind:innerWidth on:scroll="{updateVisibleHeader}" />
<div class="overlay_table2">
    <!-- <div class="table2_headline">Positionen</div> -->

    <div
        class="table"
        class:gridview="{grid == undefined ? innerWidth <= gridBreakPoint : grid}"
        class:tableview="{grid == undefined ? innerWidth > gridBreakPoint : !grid}"
    >
        <div class="tablerow thead" bind:this="{header}">
            {#if !location.pathname.includes("cart") && !sharedView && ($login?.account.roles?.includes("A") || $login?.account.permissions?.includes("B")) && (!isOperationOut(operation) || !operationEmailed(operation))}
                <div class="col mw-60 w-60 max-w-60">
                    <div class="cell_inner"></div>
                </div>
            {/if}
            <div class="col mw-140 w-140 max-w-180 hidden900">
                <div class="cell_inner">{$_("positions.image")}</div>
            </div>
            <div class="col position mw-60 max-w-60 w-60">
                <div class="cell_inner">{$_("positions.positionShort")}</div>
            </div>
            <div class="col mw-60 w-60 max-w-60">
                <div class="cell_inner">{$_("positions.quantityShort")}</div>
            </div>
            {#if !isOperationOut(operation) && operation.type == 1 && $showPrices && !sharedView}
                <div class="col max-w-40">
                    <div
                        class="cell_inner"
                        use:tooltip="{{
                            content: $_('positions.chargeable'),
                        }}"
                    >
                        V
                    </div>
                </div>
            {/if}
            {#if operation.type == 1}
                <div class="col modellgruppe hidden1100">
                    <div class="cell_inner">{$_("positions.modelGroup")}</div>
                </div>
                <div class="col mw-140 hidden960">
                    <div class="cell_inner">{$_("positions.color")}</div>
                </div>
            {:else if operation.type == 2}
                <div class="col mw-160 w-160">
                    <div class="cell_inner">{$_("positions.status")}</div>
                </div>
                <div class="col mw-60 max-w-60 w-60">
                    <div class="cell_inner">{$_("positions.cancellation")}</div>
                </div>
                {#if !isOperationOut(operation) && $showPrices && !sharedView}
                    <div class="col max-w-40">
                        <div
                            class="cell_inner"
                            use:tooltip="{{
                                content: $_('positions.chargeable'),
                            }}"
                        >
                            V
                        </div>
                    </div>
                {/if}
                <div class="col hidden1700">
                    <div class="cell_inner">{$_("positions.modelGroup")}</div>
                </div>
                <div class="col mw-140 hidden1700">
                    <div class="cell_inner">{$_("positions.color")}</div>
                </div>
                <div class="col mw-120">
                    <div class="cell_inner">{$_("positions.deliveryDateFromFactory")}</div>
                </div>
                <div class="col mw-140 w-140">
                    <div class="cell_inner paketverfolgung">{$_("positions.tracking")}</div>
                </div>
            {/if}
            {#if !isOperationOut(operation) && !sharedView}
                <div class="col ursprung hidden740 max-w-150">
                    <div class="cell_inner ursprung">{$_("positions.source")}</div>
                </div>
            {/if}
            {#if !sharedView}
                <div class="col hidden660 max-w-150">
                    <div class="cell_inner">{$_("positions.ecpNumber")}</div>
                </div>
            {/if}
            {#if !sharedView}
                <div class="col w-40 max-w-40 aligncenter">
                    <div class="cell_inner">{$_("positions.residentialUnitsShort")}</div>
                </div>
            {/if}
            {#if !sharedView && location.pathname.includes("cart")}
                <div class="col mw-150 w-150 max-w-200">
                    <div class="cell_inner">{$_("positions.infoToAgent")}</div>
                </div>
            {/if}
            {#if !sharedView && $showPrices}
                <div
                    class="col {operation.collectionName !== 'operation-out' ? 'mw-120 max-w-120' : 'mw-150 max-w-180'}"
                >
                    <div class="cell_inner">{$_("positions.unitPrice")}</div>
                </div>
            {/if}

            {#if !sharedView && $showPrices}
                <div class="col mw-100 w-100 max-w-150 rueckfrage aligncenter">
                    <div class="cell_inner">{$_("positions.actions")}</div>
                </div>
            {:else if !sharedView}
                <div class="col w-60 max-w-150">
                    <div class="cell_inner">{$_("positions.actions")}</div>
                </div>
            {/if}
        </div>

        {#each operation.positions || [] as position, idx}
            <!-- svelte-ignore a11y-click-events-have-key-events -->
            <!-- svelte-ignore a11y-no-static-element-interactions -->
            <div
                class="tablerow trow variable_height"
                class:deleted="{position.orderQuantity == 0}"
                class:active="{position.number == activePosition}"
                on:click|stopPropagation="{() => {
                    if (!overlay && !sharedView) {
                        dispatch('click', { activePosition: position.number })
                        return
                    }
                    if (activePosition == position.number) activePosition = undefined
                    else activePosition = position.number
                }}"
            >
                {#if !location.pathname.includes("cart") && !sharedView && ($login?.account.roles?.includes("A") || $login?.account.permissions?.includes("B")) && (!isOperationOut(operation) || !operationEmailed(operation))}
                    <div
                        class="{'col mw-60 w-60 max-w-60 ' + hasValueUpdates(`positions[${idx}].orderQuantity`)}"
                        use:tooltip="{{
                            content: `${filterValueUpdates(`positions[${idx}].orderQuantity`) || 'invalid'}`,
                        }}"
                    >
                        <span class="label"></span>
                        <Checkbox
                            checked="{position.orderQuantity != 0}"
                            on:checked="{() => deselectOrSelectPositionFromOperation(position)}"
                            on:unchecked="{() => deselectOrSelectPositionFromOperation(position)}"
                        />
                    </div>
                {/if}
                <div class="col position_image mw-140 max-w-180 hidden900 w-140">
                    {#if loadAttachments && operation}
                        {#await loadAttachments(operation)}
                            {#if $filesStore && $filesStore[operation?.id]?.length}
                                <PositionImageField
                                    {operation}
                                    operationType="{operation?.collectionName}"
                                    filelist="{$filesStore[operation?.id]?.filter((f) =>
                                        f.name.match(
                                            new RegExp(
                                                '^\\d{7,8}_' +
                                                    String(
                                                        operation.collectionName == 'operation'
                                                            ? position?.number
                                                            : position.origin?.positionNumber || position.number
                                                    ).padStart(3, '0') +
                                                    '-'
                                            )
                                        )
                                    )}"
                                />
                            {:else}
                                <MiniLoader />
                            {/if}
                        {:then files}
                            <PositionImageField
                                {operation}
                                operationType="{operation?.collectionName}"
                                filelist="{files?.filter((f) =>
                                    f.name.match(
                                        new RegExp(
                                            '^\\d{7,8}_' +
                                                String(
                                                    operation.collectionName == 'operation'
                                                        ? position?.number
                                                        : position.origin?.positionNumber || position.number
                                                ).padStart(3, '0') +
                                                '-'
                                        )
                                    )
                                )}"
                            />
                        {/await}
                    {/if}
                </div>
                <div
                    class="{'col position max-w-60 mw-60 w-60 ' + hasValueUpdates(`positions[${idx}].alternativeTo`)}"
                    use:tooltip="{position.alternativeTo && {
                        overflowContent: position.alternativeTo || '',
                        content: `${$_('positions.alternativeToPosition', {
                            values: { number: position.alternativeTo },
                        })} <br/> ${filterValueUpdates(`positions[${idx}].alternativeTo`)}`,
                    }}"
                >
                    <span class="label">{$_("positions.position")}</span>
                    {position.number}{#if position.alternativeTo}
                        <span style="padding-left: 10px"
                            ><Icon path="{mdiArrowDecisionAuto}" />
                            {position.alternativeTo}
                        </span>{/if}
                </div>
                <div
                    class="{'col mw-60 w-60 max-w-60 ' + hasValueUpdates(`positions[${idx}].quantity`)}"
                    use:tooltip="{{
                        content: `${filterValueUpdates(`positions[${idx}].quantity`) || 'invalid'}`,
                    }}"
                    data-cy="pos-quantity"
                >
                    <span class="label">{$_("positions.amount")}</span>
                    {position.quantity ?? ""}
                </div>
                {#if !isOperationOut(operation) && operation.type == 1 && $showPrices && !sharedView}
                    <div
                        class="{'col max-w-40 ' + hasValueUpdates(`positions[${idx}].chargeable`)}"
                        use:tooltip="{{
                            content: `${filterValueUpdates(`positions[${idx}].chargeable`) || 'invalid'}`,
                        }}"
                    >
                        <span class="label">{$_("positions.chargeable")} </span>
                        <ChargeableField
                            statusSummary="{operation.statusSummary}"
                            positions="{operation.positions}"
                            {position}
                        />
                    </div>
                {/if}
                {#if operation.type == 1}
                    <div
                        class="{'col modellgruppe hidden1100 ' + hasValueUpdates(`positions[${idx}].modelgroup`)}"
                        use:tooltip="{{
                            overflowContent: modelGroupCodesMap[position?.modelGroup]?.de || '',
                            content: `${filterValueUpdates(`positions[${idx}].modelgroup`) || 'invalid'}`,
                        }}"
                    >
                        <span class="label">{$_("positions.modelGroup")}</span>
                        {#if position.modelGroup}{position.modelGroup}
                            {#if modelGroupCodesMap[position.modelGroup]}
                                |
                                {modelGroupCodesMap[position.modelGroup].de}{/if}
                        {/if}
                    </div>
                    <div
                        class="{'col mw-140 ovf_ellipse hidden960 ' + hasValueUpdates(`positions[${idx}].color.name`)}"
                        use:tooltip="{{
                            overflowContent: position.color?.name || $_('positions.noColorSpecification'),
                            content: `${filterValueUpdates(`positions[${idx}].color.name`) || 'invalid'}`,
                        }}"
                    >
                        <span class="label">{$_("positions.color")}</span>
                        {position.color?.name || ""}
                    </div>
                {:else if operation.type == 2}
                    <div class="col lieferstatus mw-160 w-160">
                        <!-- <span class="label">Status</span> -->
                        <PositionStatusField {position} />
                    </div>
                    <div
                        class="col col_storno max-w-60 mw-60 w-60"
                        use:tooltip="{{
                            content: `${
                                position.status?.blockingIndicator == 2
                                    ? $_('positions.cancellation')
                                    : $_('positions.noCancellation')
                            }`,
                        }}"
                    >
                        <span class="state {position.status?.blockingIndicator == 2 ? 'storno' : 'grey'}"
                            >{position.status?.blockingIndicator == 2 ? "S" : ""}</span
                        >
                    </div>
                    {#if !isOperationOut(operation) && $showPrices && !sharedView}
                        <div class="col max-w-40">
                            <span class="label">{$_("positions.chargeable")}</span>
                            <ChargeableField
                                statusSummary="{operation.statusSummary}"
                                positions="{operation.positions}"
                                {position}
                            />
                        </div>
                    {/if}
                    <div
                        class="col modellgruppe hidden1700"
                        use:tooltip="{{
                            overflowContent: modelGroupCodesMap[position?.modelGroup]?.de || '',
                            content: `invalid`,
                        }}"
                    >
                        <span class="label">{$_("positions.modelGroup")}</span>
                        {#if position.modelGroup}{position.modelGroup} |
                            {modelGroupCodesMap[position.modelGroup]?.de}{/if}
                    </div>
                    <div
                        class="col mw-140 hidden1700 ovf_ellipse"
                        use:tooltip="{{
                            overflowContent: `${position.color?.name || $_('positions.noColorSpecification')}`,
                            content: `invalid`,
                        }}"
                    >
                        <span class="label">{$_("positions.color")}</span>
                        {position.color?.name || ""}
                    </div>
                    <div class="col mw-120">
                        <span class="label">{$_("positions.deliveryDateFromFactory")}</span>
                        <WeekField value="{position.delivery?.plannedWeek}" />
                    </div>
                    <div class="col paketverfolgung mw-140 w-140">
                        <div class="cell_inner">
                            <TrackingField {position} />
                        </div>
                    </div>
                {/if}
                {#if !isOperationOut(operation) && !sharedView}
                    <div
                        class="col ursprung hidden740 max-w-150"
                        use:tooltip="{{
                            overflowContent: position.origin?.operationNumber || '',
                            content: `${filterValueUpdates(`positions[${idx}].origin.operationNumber`) || 'invalid'}`,
                        }}"
                    >
                        <span class="label">{$_("positions.source")}</span>

                        {#if position.origin?.operationNumber}
                            {#await getOpByNumber(position.origin?.operationNumber)}
                                <MiniLoader />
                            {:then oOp}
                                {#if oOp}
                                    <a
                                        href="/"
                                        on:click|preventDefault|stopPropagation="{() => {
                                            if (
                                                oOp.number != operation.number ||
                                                activePosition != position.origin.positionNumber
                                            ) {
                                                openOverlay(oOp, 'details', {
                                                    activePosition: position.origin.positionNumber,
                                                })
                                            }
                                        }}"
                                        ><span class="table_icon"
                                            ><Icon path="{oOp.type == 1 ? mdiTag : mdiClipboardText}" /></span
                                        >
                                        {oOp.number}
                                    </a>
                                {:else}
                                    {position.origin.operationNumber}
                                {/if}
                            {/await}
                        {/if}
                    </div>
                {/if}
                {#if !sharedView}
                    <div
                        class="{'col hidden660 max-w-150 ' + hasValueUpdates(`positions[${idx}].rikNumber`)}"
                        use:tooltip="{{
                            overflowContent: position.rikNumber || '',
                            content: `${filterValueUpdates(`positions[${idx}].rikNumber`) || 'invalid'}`,
                        }}"
                    >
                        <span class="label">{$_("positions.ecpNumber")}</span>
                        {position.rikNumber || ""}
                    </div>
                {/if}
                {#if !sharedView}
                    <div
                        class="{'col w-40 max-w-40 aligncenter' +
                            hasValueUpdates(`positions[${idx}].residentialUnitsCount`)}"
                        use:tooltip="{{
                            overflowContent: position.residentialUnitsCount || '',
                            content: `${position.residentialUnitsCount || 'invalid'} ${$_('components.operationsFilter.residentialUnits')} <br /> ${
                                filterValueUpdates(`positions[${idx}].residentialUnitsCount`) || ''
                            }`,
                        }}"
                    >
                        <span class="label">{$_("positions.residentialUnits")}</span>
                        {position.residentialUnitsCount || ""}
                    </div>
                {/if}

                {#if !sharedView && location.pathname.includes("cart")}
                    <div class="col mw-150 w-150 max-w-200">
                        <span class="label">{$_("positions.infoToAgent")}</span>
                        <div class="inputholder haslabel">
                            <label for=""></label>
                            <input
                                type="text"
                                placeholder=""
                                style="margin: 0px; padding-top: 0px;"
                                value="{position.detailsForClerk || ''}"
                                use:tooltip="{{
                                    content: $_('positions.specialRequestNoCharge'),
                                }}"
                                on:focus="{(e) => {
                                    initValueClerk = position.detailsForClerk || ''
                                }}"
                                on:click|preventDefault|stopPropagation="{() => {}}"
                                on:blur="{(e) => {
                                    if (e.currentTarget.value == initValueClerk) return

                                    position.detailsForClerk = e.currentTarget.value
                                    operation.positions.forEach((pos) => {
                                        if (pos.number == position.number) {
                                            pos.detailsForClerk = position.detailsForClerk
                                        }
                                    })

                                    if (cart) updateCartDebounce(cartObj)
                                    dispatch('updated', { operation })
                                }}"
                            />
                        </div>
                    </div>
                {/if}

                {#if !sharedView && $showPrices}
                    {#if operation.collectionName !== "operation-out"}
                        <div
                            class="col mw-120 max-w-120 alignright"
                            use:tooltip="{{
                                overflowContent: position.netPrice || '',
                                content: {
                                    component: OperationPositionPriceSummary,
                                    props: {
                                        position,
                                        operation,
                                    },
                                },
                            }}"
                        >
                            <span class="label">{$_("positions.unitPrice")}</span>
                            <NumberField value="{position.netPrice}" unit="€" />
                        </div>
                    {:else}
                        {#key position.endCustomerPrice}
                            <!-- svelte-ignore a11y-no-static-element-interactions -->
                            <div
                                class="col mw-150 max-w-180 alignright sum"
                                on:click|stopPropagation
                                use:tooltip="{{
                                    overflowContent: position.netPrice || '',

                                    content: {
                                        component: OperationPositionPriceSummary,
                                        props: {
                                            position,
                                            operation,
                                        },
                                    },
                                }}"
                            >
                                <div class="label">{$_("positions.unitPrice")}</div>
                                {#if operationEmailed(operation)}
                                    <NumberField value="{position.endCustomerPrice}" unit="€" />
                                {:else}<div
                                        style="display: flex; gap: 10px; align-items: flex-end;"
                                        class:loss="{position.endCustomerPrice &&
                                            position.endCustomerPrice <= position.netPrice}"
                                    >
                                        <strong style="margin-bottom: 5px">{$_("positions.sellingPrice")}: </strong>
                                        <TextField
                                            type="number"
                                            label="{$_('positions.purchasePrice')}: {position.netPrice
                                                ? position.netPrice + '€'
                                                : $_('components.noOffer')} "
                                            value="{position.endCustomerPrice}"
                                            showEuroSymbol="{true}"
                                            on:blur="{(e) => {
                                                positionOutOf(position).endCustomerPrice = e.detail
                                                position = updatePriceWizard(positionOutOf(position))
                                                if (
                                                    position.endCustomerPrice &&
                                                    position.endCustomerPrice <= position.netPrice
                                                ) {
                                                    showMarginWarning = true
                                                }
                                            }}"
                                            required
                                            cssClass="transparent"
                                            decimal="{2}"
                                        />
                                    </div>{/if}
                            </div>{/key}
                    {/if}
                {/if}

                {#if !sharedView}
                    <div
                        class="col mw-100 w-100 max-w-150 action_buttons rueckfrage {operation.collectionName ==
                        'operation-out'
                            ? 'pw-140'
                            : ''}"
                    >
                        <button
                            class="btn btn_details"
                            use:tooltip="{{
                                content: $_('positions.showPositionDetails'),
                            }}"
                        >
                            <Icon path="{mdiPageNext}" size="24px" />
                            <span class="label">{$_("positions.showPositionDetails")}</span>
                        </button>

                        {#if $showPrices}
                            <button
                                class="btn inquiry"
                                data-cy="inquiry"
                                on:click|stopPropagation="{() => {
                                    openOverlay(operation, 'inquiry', {
                                        operation,
                                        position,
                                    })
                                }}"
                                use:tooltip="{{
                                    content: $_('positions.inquiryForPosition'),
                                }}"
                            >
                                <Icon path="{icons.inquiry}" size="24px" />

                                <span class="label">{$_("positions.inquiry")}</span>
                            </button>
                        {/if}
                        {#if operation.collectionName == "operation-out"}
                            <button
                                class="btn pricewizard-icon"
                                on:click|preventDefault|stopPropagation="{() => {
                                    openPricewizard(idx)
                                }}"
                                on:keydown|preventDefault|stopPropagation
                                use:tooltip="{{
                                    content: $_('components.priceWizard.priceWizard'),
                                }}"
                            >
                                <Icon path="{mdiPencilOutline}" size="24px" />
                                <span class="label">{$_("components.priceWizard.priceWizard")}</span>
                            </button>
                        {/if}
                        {#if location.pathname.includes("cart")}
                            {#if position.orderQuantity !== 0}
                                <button
                                    class="btn delete"
                                    on:click|stopPropagation="{() => {
                                        if (isOnlyActivePosition(operation, position)) {
                                            activePosition = undefined
                                            deleteOperation = asOperation(operation)
                                            return
                                        }
                                        activePosition = undefined
                                        deletePositionOperation = JSON.parse(JSON.stringify(position))
                                    }}"
                                    use:tooltip="{{
                                        content: $_('positions.deletePosition'),
                                    }}"
                                >
                                    <Icon path="{mdiTrashCanOutline}" size="24px" />
                                    <span class="label">{$_("positions.delete")}</span>
                                </button>
                            {:else}
                                <button
                                    class="btn delete"
                                    on:click|stopPropagation="{() => {
                                        activePosition = undefined
                                        restorePositionOperation = JSON.parse(JSON.stringify(position))
                                    }}"
                                    use:tooltip="{{
                                        content: $_('positions.recreatePosition'),
                                    }}"
                                >
                                    <Icon path="{mdiRestore}" size="24px" />
                                    <span class="label">{$_("positions.recreate")}</span>
                                </button>
                            {/if}
                        {/if}
                    </div>
                {/if}
                {#if !position?.additionalItems?.length && isOperationOut(operation) && !operationEmailed(operation) && !sharedView}
                    <!-- svelte-ignore missing-declaration -->
                    <button
                        class="btn btn-secondary btn-rounded add-position-service hidden-on-mobile"
                        use:tooltip="{{
                            content: $_('positions.addAdditionalService'),
                        }}"
                        on:click|stopPropagation="{() => {
                            addNewPositionService(position)
                        }}"
                    >
                        <Icon path="{mdiFormatListGroupPlus}" size="24px" />
                    </button>
                {/if}
            </div>

            {#if position.number == activePosition}
                <div
                    class="hiddeninfos"
                    class:active="{position.number == activePosition}"
                    style="height:{innerWidth > 1350 ? positionHeights[idx] + 'px' : 'auto'};"
                >
                    <PositionDetails
                        {loadAttachments}
                        bind:clientHeight="{positionHeights[idx]}"
                        {operation}
                        {position}
                        on:close="{() => {
                            activePosition = undefined
                        }}"
                        bind:activeTab="{positionTab[idx]}"
                        {sharedView}
                    />
                </div>
            {/if}
            {#if operation.collectionName === "operation-out" && !sharedView}
                <AdditionalServices
                    operationEmailed="{operationEmailed(operation)}"
                    bind:operation="{operationOut}"
                    position="{positionOutOf(position)}"
                    bind:activePosition
                    bind:grid
                    {gridBreakPoint}
                    {addNewPositionService}
                />
            {/if}
        {/each}
        {#if operation.collectionName === "operation-out" && !sharedView}
            <AdditionalPositions
                operationEmailed="{operationEmailed(operation)}"
                bind:grid
                {gridBreakPoint}
                bind:operation="{operationOut}"
            />
        {/if}
    </div>

    <!-- // Table -->
</div>
{#if deletePositionOperation}
    <Modal show="{true}" on:close="{() => (deletePositionOperation = null)}" showCloseIcon="{false}">
        <div slot="header">{$_("positions.deleteFromOperation")}</div>
        <div>
            {$_("positions.confirmDeletePosition", { values: { number: deletePositionOperation.number } })}
        </div>
        <div slot="footer">
            <button class="btn btn-secondary" on:click="{() => (deletePositionOperation = null)}">
                {$_("general.cancel")}
            </button>
            <button class="btn" on:click="{() => deselectPositionFromOperation(deletePositionOperation)}">
                {$_("general.delete")}
            </button>
        </div>
    </Modal>
{/if}

{#if deleteOperation}
    <Modal show="{true}" on:close="{() => (deleteOperation = null)}" showCloseIcon="{false}">
        <div slot="header">{$_("positions.deleteOperation")}</div>
        <div>
            {$_("positions.confirmDeleteLastPosition")}
        </div>
        <div slot="footer">
            <button class="btn btn-secondary" on:click="{() => (deleteOperation = null)}">
                {$_("general.cancel")}
            </button>
            <button class="btn" on:click="{() => deleteOperationFromCart(deleteOperation)}">
                {$_("general.delete")}
            </button>
        </div>
    </Modal>
{/if}

{#if restorePositionOperation}
    <Modal show="{true}" on:close="{() => (restorePositionOperation = null)}" showCloseIcon="{false}">
        <div slot="header">{$_("positions.restorePosition")}</div>
        <div>
            {$_("positions.confirmRestorePosition", { values: { number: restorePositionOperation.number } })}
        </div>
        <div slot="footer">
            <button class="btn btn-secondary" on:click="{() => (restorePositionOperation = null)}">
                {$_("general.cancel")}
            </button>
            <button class="btn" on:click="{() => selectPositionFromOperation(restorePositionOperation)}">
                {$_("general.restore")}
            </button>
        </div>
    </Modal>
{/if}

{#if showPricewizard !== null}
    <Modal
        size="xl"
        show="{true}"
        on:close="{closePricewizard}"
        showCloseIcon="{operationEmailed(operation)}"
    >
        <Pricewizard
            operation="{operationOut}"
            readonly="{operationEmailed(operation)}"
            position="{operation.positions[showPricewizard]}"
            on:close="{closePricewizard}"
            on:apply="{(e) => {
                // Pricewizard arbeitet auf einem eigenen Clone der Position und
                // liefert in `operation` bereits alle (auch per PriceCalc
                // berechneten) Werte. Nur die manuell im Wizard gesetzten
                // Preisfelder werden übernommen, damit die PriceCalc-Ergebnisse
                // der übrigen Felder nicht überschrieben werden.
                operation = e.detail.operation
                if (showPricewizard !== null && e.detail.position) {
                    const target = asOperationOut(operation).positions[showPricewizard]
                    target.endCustomerPrice = e.detail.position.endCustomerPrice
                    target.endCustomerPriceGross = e.detail.position.endCustomerPriceGross
                    target.priceWizard = e.detail.position.priceWizard
                }
            }}"
        />
    </Modal>
{/if}

{#if showMarginWarning}
    <Modal
        show="{true}"
        size="sm"
        showCloseIcon="{false}"
        on:close="{() => {
            showMarginWarning = false
        }}"
    >
        <div slot="header">{$_("general.warning")}</div>
        <p>{$_("positions.marginWarning")}</p>
        <div slot="footer">
            <div style="display: flex; justify-content: space-between; width: 100%;">
                <button
                    class="btn"
                    on:click|preventDefault|stopPropagation="{() => {
                        showMarginWarning = false
                    }}"
                >
                    {$_("general.ok")}
                </button>
            </div>
        </div>
    </Modal>
{/if}
